import React, { useEffect, useRef, useState, Fragment } from 'react';
import { Button, Spinner, Container, Card, Row, Col } from 'react-bootstrap';
import { Device } from '@twilio/voice-sdk';
import { toast } from 'react-toastify';
import styled from '@emotion/styled';
import { FaPhone } from 'react-icons/fa';
import { AiOutlineAudio, AiOutlineAudioMuted } from 'react-icons/ai';
import { MdAddCall } from 'react-icons/md';
import { connect } from 'react-redux';
import { Global, css } from '@emotion/react';
import dayjs from 'dayjs';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import {
    bargeIntoCallAction,
    clearActiveCall,
    endCallAction,
    listenToCallAction,
} from '@/_redux/actions/ActionCallCenter';
import { callCenterApi } from '@/_services/api/callCenter';
import { CallStatusBadge } from './CampaignMetricsModal';
import CallCenterNote from './CallCenterNote';

const AUTO_REFRESH_INTERVAL = 5000;

const CallContainer = styled.div`
    height: 100%;
    .call-visualization {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100px;
        margin: 20px 0;
        position: relative;
    }

    .sound-wave {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        height: 60px;
    }

    .bar {
        width: 4px;
        background: #007bff;
        border-radius: 2px;
        animation: soundWave 3s ease-in-out infinite;
    }

    @keyframes soundWave {
        0%,
        100% {
            height: 10px;
        }
        50% {
            height: 60px;
        }
    }

    .call-status {
        text-align: center;
        margin: 20px 0;
    }

    .call-timer {
        font-size: 24px;
        font-weight: bold;
        color: #28a745;
        margin: 10px 0;
    }

    .phone-ui {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border-radius: 20px;
        padding: 20px;
        color: white;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        min-width: 600px;
        margin: 0 auto;
    }

    .call-controls {
        display: flex;
        justify-content: center;
        gap: 20px;
        margin-top: 30px;
    }

    .call-btn {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
            transform: scale(1.1);
        }
    }

    .hangup-btn {
        background: #dc3545;
        color: white;
    }

    .leave-btn {
        background: #ffa500;
        color: white;
    }

    .barge-btn {
        background: #28a745;
        color: white;
    }

    .status-indicator {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 8px;

        &.connecting {
            background: #ffc107;
            animation: pulse 1.5s infinite;
        }

        &.connected {
            background: #28a745;
            animation: pulse 2s infinite;
        }

        &.disconnected {
            background: #dc3545;
        }
    }

    @keyframes pulse {
        0% {
            transform: scale(0.8);
            opacity: 0.5;
        }
        50% {
            transform: scale(1.2);
            opacity: 1;
        }
        100% {
            transform: scale(0.8);
            opacity: 0.5;
        }
    }
`;

const SummaryCard = styled(Card)`
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #fff;
    box-shadow: none;
    backdrop-filter: blur(8px);
    border-radius: 6px;

    .card-body {
        padding: 1rem 1.1rem;
    }

    h6,
    p,
    strong {
        color: #fff;
    }

    .text-muted {
        color: rgba(255, 255, 255, 0.8) !important;
    }
`;

const modalGlobalStyles = css`
    .modal-dialog.active-call-modal-dialog {
        max-width: min(96vw, 1700px) !important;
        width: min(96vw, 1700px) !important;
        margin: 1rem auto !important;
    }

    .modal-dialog.active-call-modal-dialog .modal-content {
        min-height: 90vh;
        overflow: hidden;
    }

    .modal-dialog.active-call-modal-dialog .modal-body {
        padding: 1rem;
        overflow-x: hidden;
    }

    .active-call-content .row {
        margin-left: 0;
        margin-right: 0;
    }
`;

const formatCallDuration = (startedAt, endedAt) => {
    if (!startedAt || !endedAt) return '0:00';

    const start = dayjs(startedAt);
    const end = dayjs(endedAt);
    const diffInSeconds = end.diff(start, 'second');

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const ActiveCallComponent = ({ callId, endCallAction, bargeIntoCallAction = () => {}, clearActiveCall }) => {
    const [isMuted, setIsMuted] = useState(false);
    const [callConnection, setCallConnection] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [callDuration, setCallDuration] = useState(0);
    const [activeCall, setActiveCall] = useState({});
    const [isLoadingCall, setIsLoadingCall] = useState(false);
    const [isBargeConnecting, setIsBargeConnecting] = useState(false);
    const deviceRef = useRef(null);
    const callRef = useRef(null);
    const timerRef = useRef(null);
    const connectionSessionRef = useRef(0);

    const cleanupTwilioDevice = (resetConnection = false) => {
        connectionSessionRef.current += 1;
        if (callRef.current) {
            try {
                callRef.current.disconnect();
            } catch (error) {
                console.error('Error disconnecting call:', error);
            }
            callRef.current = null;
        }

        if (deviceRef.current) {
            try {
                deviceRef.current.destroy();
            } catch (error) {
                console.error('Error destroying device:', error);
            }
            deviceRef.current = null;
        }

        setConnectionStatus('disconnected');
        setIsMuted(false);

        if (resetConnection) {
            setCallConnection(null);
        }
    };

    const fetchActiveCall = async () => {
        try {
            setIsLoadingCall(true);
            if (!callId || isLoadingCall) return;

            const response = await callCenterApi.getCall(callId);
            setActiveCall(response);
            if (response?.callStatus === 'completed') {
                cleanupTwilioDevice(true);
                clearActiveCall();
            } else if (response?.callStatus !== 'in_progress' && response?.callStatus !== 'ringing') {
                setConnectionStatus('disconnected');
            }
        } catch (error) {
            console.error('Error fetching active:', error);
            toast.error('Could not fetch call details.');
        } finally {
            setIsLoadingCall(false);
        }
    };

    const initializeTwilioDevice = async (connection) => {
        console.log('Initializing Twilio Device for active call');
        setConnectionStatus('connecting');
        const sessionId = ++connectionSessionRef.current;

        try {
            const device = new Device(connection.token, {
                logLevel: 1,
                codecPreferences: ['opus', 'pcmu'],
            });

            device.on('registered', async () => {
                if (sessionId !== connectionSessionRef.current) return;
                console.log('Twilio Device Registered for active call');

                if (!connection.conferenceName) {
                    console.error('Missing conference name');
                    toast.error('Connection failed: Missing conference details');
                    return;
                }

                try {
                    const call = await device.connect({
                        params: {
                            conferenceName: connection.conferenceName,
                            muted: 'false',
                        },
                    });
                    if (sessionId !== connectionSessionRef.current) return;
                    callRef.current = call;

                    call.on('accept', () => {
                        if (sessionId !== connectionSessionRef.current) return;
                        console.log('Call accepted (connected to conference)');
                        setConnectionStatus('connected');
                        setIsMuted(false);
                    });

                    call.on('disconnect', () => {
                        if (sessionId !== connectionSessionRef.current) return;
                        console.log('Call disconnected');
                        setConnectionStatus('disconnected');
                    });

                    call.on('error', (err) => {
                        if (sessionId !== connectionSessionRef.current) return;
                        console.error('Call error:', err);
                        toast.error('Call connection error');
                    });
                } catch (err) {
                    if (sessionId !== connectionSessionRef.current) return;
                    console.error('Error connecting device:', err);
                    toast.error('Failed to connect: ' + err.message);
                }
            });

            device.on('error', (error) => {
                if (sessionId !== connectionSessionRef.current) return;
                console.error('Device error:', error);
                toast.error(`Connection error: ${error.message}`);
                setConnectionStatus('disconnected');
            });

            device.on('connect', () => {
                if (sessionId !== connectionSessionRef.current) return;
                console.log('Connected to conference');
                toast.success('Connected to call audio');
                setConnectionStatus('connected');
            });

            device.register();
            deviceRef.current = device;
        } catch (err) {
            console.error('Failed to initialize device:', err);
            toast.error('Failed to initialize audio device');
            setConnectionStatus('disconnected');
        }
    };

    const handleBargeIn = async () => {
        if (!callId) return null;

        setIsBargeConnecting(true);
        try {
            const connection = await bargeIntoCallAction(callId);
            if (connection?.token) {
                setCallConnection(connection);
            }
            return connection;
        } catch (error) {
            return null;
        } finally {
            setIsBargeConnecting(false);
        }
    };

    const handleToggleMute = () => {
        if (callRef.current) {
            if (callRef.current.isMuted()) {
                callRef.current.mute(false);
                setIsMuted(false);
                toast.info('Call unmuted');
            } else {
                callRef.current.mute(true);
                setIsMuted(true);
                toast.info('Call muted');
            }
        } else {
            console.warn('No active call to mute');
        }
    };

    const handleInternalHangup = async () => {
        try {
            await endCallAction(callId);
        } catch (error) {
            toast.error(error?.message || 'failed to end call');
        } finally {
            setConnectionStatus('disconnected');
            cleanupTwilioDevice(true);
            clearActiveCall();
            fetchActiveCall();
        }
    };

    useEffect(() => {
        if (!callId) return undefined;

        fetchActiveCall();
        const interval = setInterval(() => {
            fetchActiveCall();
        }, AUTO_REFRESH_INTERVAL);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callId]);

    useEffect(() => {
        if (activeCall && activeCall?.startedAt && activeCall?.callStatus === 'in_progress') {
            const startTime = new Date(activeCall.startedAt);
            const updateTimer = () => {
                const now = new Date();
                const duration = Math.floor((now - startTime) / 1000);
                setCallDuration(duration);
            };

            updateTimer();
            timerRef.current = setInterval(updateTimer, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [activeCall]);

    useEffect(() => {
        if (callConnection?.token) {
            initializeTwilioDevice(callConnection);
        }

        return () => {
            cleanupTwilioDevice();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callConnection]);

    useEffect(
        () => () => {
            cleanupTwilioDevice(true);
            clearActiveCall();
        },
        [clearActiveCall]
    );

    const renderSoundWave = () => {
        const bars = [];
        for (let i = 0; i < 20; i++) {
            const delay = i * 0.1;
            bars.push(
                <div
                    key={i}
                    className="bar"
                    style={{
                        animationDelay: `${delay}s`,
                        height: '60px',
                        background: '#28a745',
                    }}
                />
            );
        }
        return bars;
    };

    const isActiveCall = activeCall?.callStatus === 'in_progress' || activeCall?.callStatus === 'ringing';
    const contactData = activeCall?.contactData || activeCall?.contact;
    const presetData = activeCall?.presetData || activeCall?.preset;
    const dialedByUserData = activeCall?.dialedByUserData || activeCall?.dialedByUser;
    const bargeLoadingState = isBargeConnecting;
    const displayCallDuration = `${Math.floor(callDuration / 60)
        .toString()
        .padStart(2, '0')}:${(callDuration % 60).toString().padStart(2, '0')}`;

    return (
        <>
            <Global styles={modalGlobalStyles} />
            <div className="active-call-content">
                <Container fluid className="p-0">
                    <Row className="gx-3 gy-3">
                        <Col lg={7} md={12}>
                            <CallContainer>
                                <div className="phone-ui h-100 w-100 d-flex flex-col">
                                    <div className="w-100">
                                        <div>
                                            <h4 className="mb-3">
                                                {contactData?.phone || '-'} ({contactData?.name || 'Unknown'})
                                            </h4>

                                            {isActiveCall ? (
                                                <Fragment>
                                                    <div className="caller-info">
                                                        {activeCall?.callStatus === 'ringing' ? (
                                                            <div className="call-timer">Call is Ringing...</div>
                                                        ) : (
                                                            <Fragment>
                                                                <div className="call-timer">{displayCallDuration}</div>
                                                                <div className="call-visualization">
                                                                    <div className="sound-wave">
                                                                        {renderSoundWave()}
                                                                    </div>
                                                                </div>
                                                            </Fragment>
                                                        )}
                                                    </div>

                                                    <div className="call-status">
                                                        <div className="mb-2">
                                                            <span
                                                                className={`badge ${
                                                                    activeCall?.callStatus === 'in_progress'
                                                                        ? 'bg-success'
                                                                        : 'bg-warning'
                                                                }`}
                                                            >
                                                                {activeCall?.callStatus?.replace('_', ' ') || '-'}
                                                            </span>
                                                        </div>
                                                        <div className="mb-2">
                                                            <small className="text-light">
                                                                <strong>Preset:</strong>
                                                                <span className="ml-1">
                                                                    {presetData?.title || 'No preset'}
                                                                </span>
                                                            </small>
                                                        </div>
                                                    </div>
                                                </Fragment>
                                            ) : (
                                                <p>
                                                    {activeCall?.callStatus === 'completed' ? (
                                                        <h2>
                                                            <span>Call Ended :</span>
                                                            <span className="ml-2">
                                                                {formatCallDuration(
                                                                    activeCall?.startedAt,
                                                                    activeCall?.endedAt
                                                                )}
                                                            </span>
                                                        </h2>
                                                    ) : (
                                                        <div>
                                                            <span className="mr-2">The call is not active</span>
                                                            <CallStatusBadge status={activeCall?.callStatus} />
                                                        </div>
                                                    )}
                                                </p>
                                            )}

                                            <div className="call-controls">
                                                {activeCall?.callStatus === 'in_progress' && (
                                                    <div
                                                        className="d-flex justify-content-center mt-4 "
                                                        style={{ gap: '8px' }}
                                                    >
                                                        {connectionStatus === 'connected' && (
                                                            <Button
                                                                variant="outline-primary"
                                                                onClick={handleToggleMute}
                                                                disabled={connectionStatus !== 'connected'}
                                                                type="button"
                                                                style={{
                                                                    borderRadius: '8px',
                                                                    width: '60px',
                                                                    backgroundColor: '#fff',
                                                                }}
                                                            >
                                                                {isMuted ? (
                                                                    <AiOutlineAudioMuted color="#50aeb0" />
                                                                ) : (
                                                                    <AiOutlineAudio color="#50aeb0" />
                                                                )}
                                                            </Button>
                                                        )}

                                                        <Button
                                                            variant="outline-secondary"
                                                            onClick={handleBargeIn}
                                                            disabled={
                                                                bargeLoadingState || connectionStatus !== 'disconnected'
                                                            }
                                                            size="lg"
                                                            className="d-flex align-items-center gap-2"
                                                            style={{
                                                                borderRadius: '8px',
                                                            }}
                                                            type="button"
                                                        >
                                                            {bargeLoadingState ? (
                                                                <>
                                                                    <Spinner
                                                                        size="sm"
                                                                        animation="border"
                                                                        className="mr-1"
                                                                    />
                                                                    Connecting...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <MdAddCall />
                                                                    <span className="ml-1">Barge In</span>
                                                                </>
                                                            )}
                                                        </Button>
                                                        {activeCall?.callStatus !== 'completed' && (
                                                            <Button
                                                                variant="danger"
                                                                onClick={handleInternalHangup}
                                                                disabled={false}
                                                                type="button"
                                                                style={{
                                                                    borderRadius: '8px',
                                                                    width: '60px',
                                                                }}
                                                            >
                                                                <FaPhone className="mb-1" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mt-5">
                                            <Row>
                                                <Col md={6} style={{ padding: 2 }}>
                                                    <SummaryCard className="h-100">
                                                        <Card.Body>
                                                            <h6 className="text-muted text-uppercase mb-3">
                                                                Call Information
                                                            </h6>
                                                            <p className="mb-2">
                                                                <strong>Dialed At:</strong>{' '}
                                                                {activeCall?.createdAt
                                                                    ? new Date(activeCall?.createdAt).toLocaleString()
                                                                    : '-'}
                                                            </p>
                                                            <p className="mb-1">
                                                                <strong>Dialed by:</strong>{' '}
                                                                {dialedByUserData?.name || '-'}
                                                            </p>
                                                        </Card.Body>
                                                    </SummaryCard>
                                                </Col>
                                                <Col md={6} style={{ padding: 2 }}>
                                                    <SummaryCard className="h-100">
                                                        <Card.Body>
                                                            <h6 className="text-muted text-uppercase mb-3">
                                                                Audio Connection
                                                            </h6>
                                                            <p className="mb-2">
                                                                <strong>Conference:</strong>{' '}
                                                                {callConnection?.conferenceName || 'N/A'}
                                                            </p>
                                                            <p>
                                                                <strong className="me-2">My Status:</strong>
                                                                <small
                                                                    className={`ml-2 badge bg-${
                                                                        connectionStatus === 'connected'
                                                                            ? 'success'
                                                                            : connectionStatus === 'connecting'
                                                                              ? 'warning'
                                                                              : 'secondary'
                                                                    }`}
                                                                >
                                                                    {connectionStatus}
                                                                </small>
                                                            </p>
                                                        </Card.Body>
                                                    </SummaryCard>
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>
                                </div>
                            </CallContainer>
                        </Col>

                        <Col
                            lg={5}
                            md={12}
                            style={{ minHeight: 'calc(80vh - 120px)', maxHeight: 'calc(90vh - 120px)' }}
                        >
                            <Card className="shadow-sm border-0 h-100">
                                <Card.Body className="d-flex flex-column h-100">
                                    <CallCenterNote callId={callId} />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

const mapDispatchToProps = {
    endCallAction,
    bargeIntoCallAction,
    listenToCallAction,
    clearActiveCall,
};

const Wrapped = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <ActiveCallComponent {...props} />
    </ComponentErrorBoundary>
);

export default connect(null, mapDispatchToProps)(Wrapped);
